import { useCallback } from 'react';

import { Modal, type ModalProps } from 'antd';

import { MODAL_WIDTHS } from '@/shared/lib/constants';

import styles from './BaseModal.module.css';

const ANT_MODAL_CLOSE_SELECTOR = '.ant-modal-close';
const ANT_MODAL_CONTENT_SELECTOR = '.ant-modal-content';

type BaseModalProps = {
  title: string;
  open: boolean;
  onClose: () => void;
  /** X 버튼 클릭 시 호출. 미제공 시 onClose 사용 */
  onCloseIcon?: () => void;
  onOk?: () => void;
  okText?: string | React.ReactNode;
  cancelText?: string | React.ReactNode;
  confirmLoading?: boolean;
  width?: number;
  children: React.ReactNode;
} & Omit<ModalProps, 'title' | 'open' | 'onCancel' | 'onOk' | 'okText' | 'cancelText' | 'confirmLoading' | 'width'>;

export const BaseModal = ({
  title,
  open,
  onClose,
  onCloseIcon,
  onOk,
  okText,
  cancelText = '취소',
  confirmLoading,
  width = MODAL_WIDTHS.STANDARD_FORM,
  children,
  ...props
}: BaseModalProps) => {
  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const target = e.target as HTMLElement;
      const isContentClick = target.closest(ANT_MODAL_CONTENT_SELECTOR);
      const isCloseIcon = target.closest(ANT_MODAL_CLOSE_SELECTOR);

      if (!isContentClick || isCloseIcon) {
        (onCloseIcon ?? onClose)();
      } else {
        onClose();
      }
    },
    [onClose, onCloseIcon]
  );

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={confirmLoading}
      width={width}
      className={styles.modal}
      {...props}>
      {children}
    </Modal>
  );
};
